import React from "react";
import { Button } from "@material-tailwind/react";
import * as pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import logo from "../../images/logo/logo.png";
import { TDocumentDefinitions } from "pdfmake/interfaces";

// Configurando as fontes no pdfMake
(pdfMake as any).vfs = pdfFonts.vfs;

// Tipos para os horários faltantes
interface HorarioFaltante {
  hr_inicio: string;
}

interface HorariosFaltantesPorSala {
  [n_sala: string]: {
    [dt_evento: string]: {
      [dds: string]: {
        [turno: string]: {
          [atividade: string]: {
            [terapeuta: string]: HorarioFaltante[];
          };
        };
      };
    };
  };
}

interface HorariosFaltantesProps {
  data: HorariosFaltantesPorSala;
  loading: boolean;
  turnoSelected: string; // Nova prop para o turno selecionado
  atividadeSelected: string; // Nova prop para a atividade selecionada
}

const RelAgendaVagas: React.FC<HorariosFaltantesProps> = ({ 
  data, 
  loading, 
  turnoSelected, 
  atividadeSelected // Alterando para atividadeSelected
}) => {
  // Função para formatar a data no formato dd/mm/aaaa
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Mapa de ordenação dos dias da semana
  const ordemDiasSemana: { [key: string]: number } = {
    "Segunda-Feira": 1,
    "Terça-Feira": 2,
    "Quarta-Feira": 3,
    "Quinta-Feira": 4,
    "Sexta-Feira": 5,
    "Sábado": 6,
    "Domingo": 7,
  };

  const generatePDF = () => {
    const content: Array<any> = [
      {
        image: logo,
        width: 150,
        alignment: "center",
        margin: [0, 0, 0, 20],
      },
      {
        text: `Horários disponíveis Agenda Oficial - ${turnoSelected.toUpperCase()}`,
        style: "header",
        alignment: "center",
      },
    ];
  
    // Agrupar por dia da semana (dds) e horário de início (hr_inicio)
    const groupedByDDSAndHrInicio: { 
      [dds: string]: { 
        [hr_inicio: string]: Array<{ n_sala: string, atividade: string, terapeuta: string }> 
      } 
    } = {};
  
    Object.entries(data).forEach(([n_sala, eventos]) => {
      Object.entries(eventos).forEach(([dt_evento, ddsData]) => {
        const formattedDate = formatDate(dt_evento);
  
        Object.entries(ddsData).forEach(([dds, turnos]) => {
          if (turnos[turnoSelected]) {
            Object.entries(turnos[turnoSelected]).forEach(([atividade, terapeutas]) => {
              // Verifica se atividadeSelected está vazio ou se a atividade é a selecionada
              if ((atividadeSelected === "" || atividadeSelected === atividade) && terapeutas && Object.keys(terapeutas).length > 0) {
                Object.entries(terapeutas).forEach(([terapeuta, horarios]) => {
                  if (horarios && horarios.length > 0) {
                    horarios.forEach((horario: HorarioFaltante) => {
                      if (!groupedByDDSAndHrInicio[dds]) {
                        groupedByDDSAndHrInicio[dds] = {};
                      }
                      if (!groupedByDDSAndHrInicio[dds][horario.hr_inicio]) {
                        groupedByDDSAndHrInicio[dds][horario.hr_inicio] = [];
                      }
                      groupedByDDSAndHrInicio[dds][horario.hr_inicio].push({
                        n_sala,
                        atividade,
                        terapeuta,
                      });
                    });
                  }
                });
              }
            });
          }
        });
      });
    });
  
    // Ordenar os dias da semana
    const diasSemanaOrdenados = Object.keys(groupedByDDSAndHrInicio).sort((a, b) => {
      return ordemDiasSemana[a] - ordemDiasSemana[b];
    });
  
    // Adicionando os dados filtrados no conteúdo do PDF
    diasSemanaOrdenados.forEach((dds) => {
      const horarios = groupedByDDSAndHrInicio[dds];
      content.push({
        text: `Dia da Semana: ${dds.toUpperCase()}`,
        style: "header",
        alignment: "center",
        margin: [0, 20, 0, 10],
      });

      // Ordenar os horários dentro de cada dia
      const horariosOrdenados = Object.keys(horarios).sort();
      horariosOrdenados.forEach((hr_inicio) => {
        const items = horarios[hr_inicio];
        content.push({
          text: `Horário: ${hr_inicio}`,
          style: "subheader",
          alignment: "center",
          margin: [0, 10, 0, 5],
        });

        const tableBody: Array<Array<any>> = [
          [
            { text: "Sala", style: "tableHeader", alignment: "center" },
            { text: "Atividade - Terapeuta", style: "tableHeader", alignment: "center" },
          ],
        ];
    
        items.forEach(({ n_sala, atividade, terapeuta }) => {
          tableBody.push([
            { text: n_sala, alignment: "center" },
            { text: `${atividade} - ${terapeuta}`, alignment: "center" },
          ]);
        });
    
        content.push({
          table: {
            headerRows: 1,
            widths: ["50%", "50%"],
            body: tableBody,
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            paddingLeft: () => 5,
            paddingTop: () => 2,
            paddingBottom: () => 2,
          },
          dontBreakRows: true,
          margin: [0, 10, 0, 20],
        });
      });
    });
  
    const documentDefinition: TDocumentDefinitions = {
      content,
      styles: {
        header: {
          fontSize: 22,
          bold: true,
          margin: [0, 0, 0, 20],
          alignment: "center",
        },
        subheader: {
          fontSize: 18,
          bold: true,
          margin: [0, 10, 0, 5],
          alignment: "center",
        },
        tableHeader: {
          fontSize: 12,
          bold: true,
          alignment: "center",
        },
      },
      defaultStyle: {
        fontSize: 10,
        alignment: "center",
      },
      pageMargins: [40, 60, 40, 60],
    };
  
    pdfMake.createPdf(documentDefinition).open();
  };
  
  return (
    <div className="mt-6 text-center">
      <button
        onClick={generatePDF}
        disabled={loading}
        className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring focus:ring-blue-500 focus:ring-opacity-50"
      >
        Relatório Horários Disponíveis:
        <br/>
        <span className="text-xs">
        {turnoSelected.charAt(0).toUpperCase() + turnoSelected.slice(1).toLowerCase()}
        </span>
        <br/>
        <span className="text-xs">
        {atividadeSelected.charAt(0).toUpperCase() + atividadeSelected.slice(1).toLowerCase()}
        </span>
       </button>
    </div>
  );
};

export default RelAgendaVagas;